import {FormEvent, useContext, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {Navigate} from 'react-router-dom';

import {Box, Container, Grid, InputLabel, Link, TextField, Typography} from '@mui/material';

import LinkedInSignup from '../components/LinkedInSignup/LinkedInSignup';
import {useLogIn} from '../hooks/useLogIn';

import CopyrightLink from '../../../../components/CopyrightLink/CopyrightLink';
import {EmpionButton} from '../../../../components/EmpionButton/EmpionButton';
import {UserContext} from '../../../../contexts/UserContext/UserContext';
import {greenColors, orangeColors, primary} from '../../../../layouts/theme/designSystem';
import {AppConfig} from '../../../../lib/AppConfig';
import {User} from '../../../../types/User';
import {getTextFieldStyle} from '../../../user/styles/getTextFieldStyle';

interface Props {
  onTwoFactorRequired?: (user: User) => void;
}

export default function LogInForm({onTwoFactorRequired}: Props) {
  const {t} = useTranslation();
  const {currentUser} = useContext(UserContext);
  const {logIn, isLoading} = useLogIn({onTwoFactorRequired});

  const [input, setInput] = useState({
    email: '',
    password: '',
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setInput((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    logIn({user: input});
  };

  if (currentUser) {
    return <Navigate to={{pathname: AppConfig.DASHBOARD_PATH}}></Navigate>;
  }

  const isFormValid = Object.values(input).every((value) => value !== '');

  return (
    <Container component='main' maxWidth='sm'>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          padding: 3,
          borderRadius: 3,
        }}
      >
        <Typography variant='h4' color='black' sx={{mb: 3}}>
          {t('registration.logIn.formLabels.logIn')}
        </Typography>
        <Box component='form' onSubmit={handleSubmit} noValidate>
          <Box
            sx={{
              mb: 3,
            }}
          >
            <LinkedInSignup />
          </Box>
          {/* Divider */}
          <Box sx={{display: 'flex', alignItems: 'center', mb: 3}}>
            <Box sx={{flexGrow: 1, height: '1px', backgroundColor: primary[70]}} />
            <Typography variant='body2' color='black' sx={{mx: 2, fontWeight: 500}}>
              {t('shared.or')}
            </Typography>
            <Box sx={{flexGrow: 1, height: '1px', backgroundColor: primary[70]}} />
          </Box>
          <InputLabel htmlFor='email'>
            <Typography variant='body1' color='black'>
              {t('registration.logIn.formLabels.email')}*
            </Typography>
          </InputLabel>
          <TextField
            required
            variant='outlined'
            fullWidth
            id='email'
            name='email'
            value={input.email}
            autoComplete='email'
            autoFocus
            onChange={handleInputChange}
            sx={getTextFieldStyle()}
            InputProps={{
              sx: {
                borderColor: orangeColors[5],
                backgroundColor: orangeColors[7],
                color: greenColors[700],
              },
            }}
          />
          <InputLabel htmlFor='password'>
            <Typography variant='body1' color='black'>
              {t('registration.logIn.formLabels.password')}*
            </Typography>
          </InputLabel>
          <TextField
            required
            variant='outlined'
            fullWidth
            name='password'
            value={input.password}
            type='password'
            id='password'
            autoComplete='current-password'
            onChange={handleInputChange}
            sx={getTextFieldStyle()}
            InputProps={{
              sx: {
                borderColor: orangeColors[5],
                backgroundColor: orangeColors[7],
                color: greenColors[700],
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 3,
              mb: 2,
            }}
          >
            <EmpionButton type='submit' fullWidth disabled={!isFormValid || isLoading}>
              {t('registration.logIn.formLabels.logIn')}
            </EmpionButton>
          </Box>

          <Grid container justifyContent='space-between' sx={{flexDirection: {sm: 'column', md: 'row'}}}>
            <Grid item>
              <Link
                href={AppConfig.FORGOT_PASSWORD_PATH}
                sx={{verticalAlign: 'text-top'}}
                variant='body2'
                color='primary.dark'
              >
                {t('registration.logIn.formLabels.forgotPassword')}
              </Link>
            </Grid>
            <Grid item>
              <Grid container flexDirection='column'>
                <Link href={AppConfig.HOME_PATH} variant='body2' color='primary.dark' fontWeight={700}>
                  {t('registration.logIn.formLabels.newAccount')}
                </Link>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <CopyrightLink sx={{mt: '8px'}} />
    </Container>
  );
}
